/**
 * MentionDropdown — floating list that appears when the user types `@` in the
 * chat composer. Groups mentionable items by type and supports keyboard nav.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

export interface MentionItem {
  id: string;
  type: 'attachment' | 'ad' | 'persona';
  label: string;
  sublabel?: string;
  thumbnailUrl?: string;
}

export interface MentionDropdownProps {
  items: MentionItem[];
  query: string;
  onSelect: (item: MentionItem) => void;
  onClose: () => void;
  visible: boolean;
}

const CATEGORY_META: Record<MentionItem['type'], { icon: string; heading: string }> = {
  attachment: { icon: '📎', heading: 'Attachments' },
  ad: { icon: '🖼', heading: 'Ads in Pack' },
  persona: { icon: '👤', heading: 'Personas' },
};

const CATEGORY_ORDER: MentionItem['type'][] = ['attachment', 'ad', 'persona'];
const MAX_PER_CATEGORY = 10;

export function MentionDropdown({ items, query, onSelect, onClose, visible }: MentionDropdownProps) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const { flatItems, groups } = useMemo(() => {
    const lowerQuery = query.toLowerCase();
    const filtered = lowerQuery
      ? items.filter((i) => i.label.toLowerCase().includes(lowerQuery))
      : items;

    const grouped = new Map<MentionItem['type'], { shown: MentionItem[]; total: number }>();
    for (const type of CATEGORY_ORDER) {
      const bucket = filtered.filter((i) => i.type === type);
      if (bucket.length > 0) {
        grouped.set(type, {
          shown: bucket.slice(0, MAX_PER_CATEGORY),
          total: bucket.length,
        });
      }
    }

    const flat: MentionItem[] = [];
    for (const type of CATEGORY_ORDER) {
      const g = grouped.get(type);
      if (g) flat.push(...g.shown);
    }

    return { flatItems: flat, groups: grouped };
  }, [items, query]);

  // Reset selection when the list changes
  useEffect(() => {
    setSelectedIdx(0);
  }, [flatItems.length, query]);

  // Scroll the selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-selected="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  // Keyboard navigation
  useEffect(() => {
    if (!visible) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx((prev) => Math.min(prev + 1, flatItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (flatItems[selectedIdx]) onSelect(flatItems[selectedIdx]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }

    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [visible, flatItems, selectedIdx, onSelect, onClose]);

  if (!visible || flatItems.length === 0) return null;

  let globalIdx = 0;

  return (
    <div
      ref={listRef}
      className="absolute bottom-full left-0 mb-1 w-[280px] max-h-[320px] overflow-y-auto bg-white rounded-lg shadow-lg border border-slate-200 z-50"
    >
      {CATEGORY_ORDER.map((type) => {
        const group = groups.get(type);
        if (!group) return null;
        const { icon, heading } = CATEGORY_META[type];
        const overflow = group.total - group.shown.length;

        return (
          <div key={type}>
            <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-400 bg-slate-50 border-b border-slate-100 sticky top-0">
              {icon} {heading}
            </div>
            {group.shown.map((item) => {
              const idx = globalIdx++;
              const isSelected = idx === selectedIdx;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-selected={isSelected}
                  onClick={() => onSelect(item)}
                  className={`w-full text-left px-3 py-1.5 flex items-center gap-2 text-sm transition-colors ${
                    isSelected ? 'bg-slate-100' : 'hover:bg-slate-50'
                  }`}
                >
                  <span className="text-xs shrink-0">{CATEGORY_META[item.type].icon}</span>
                  <span className="truncate text-slate-800">{item.label}</span>
                  {item.sublabel && (
                    <span className="ml-auto text-[10px] text-slate-400 truncate max-w-[90px]">
                      {item.sublabel}
                    </span>
                  )}
                </button>
              );
            })}
            {overflow > 0 && (
              <div className="px-3 py-1 text-[10px] text-slate-400 italic">
                and {overflow} more…
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
